import { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { DatePicker } from '../../components/DatePicker';
import { issueSubscription, ApiError } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import { todayLocal } from '../../lib/time';
import type { SubscriptionPlan } from '../../lib/types';

const PLAN_DEFAULT_HOURS: Record<SubscriptionPlan, number> = {
  starter: 8,
  amateur: 16,
  advanced: 24,
  master: 40,
  unlimited: 80,
};

interface Props {
  open: boolean;
  userId: string;
  firstName: string;
  onClose: () => void;
  onIssued: () => void;
}

export function IssueSubscriptionDialog({ open, userId, firstName, onClose, onIssued }: Props) {
  const { push } = useToast();
  const [plan, setPlan] = useState<SubscriptionPlan>('amateur');
  const [hours, setHours] = useState<string>('16');
  const [startsOn, setStartsOn] = useState(todayLocal());
  const [durationDays, setDurationDays] = useState<string>('30');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPlan('amateur');
      setHours('16');
      setStartsOn(todayLocal());
      setDurationDays('30');
      setBusy(false);
    }
  }, [open]);

  const onPlanChange = (next: SubscriptionPlan) => {
    setPlan(next);
    setHours(String(PLAN_DEFAULT_HOURS[next]));
  };

  // The admin enters a duration in days; the end date is derived from it, so any
  // duration (30 / 60 / 90 / custom) produces a correct expires_at (epic #67).
  const durationNum = Number(durationDays);
  const durationValid = Number.isInteger(durationNum) && durationNum >= 1;
  const expiresOn = durationValid ? addDays(startsOn, durationNum) : startsOn;

  const handleIssue = async () => {
    const n = Number(hours);
    if (!Number.isFinite(n) || n < 1) {
      push('error', 'Hours must be >= 1.');
      return;
    }
    if (!durationValid) {
      push('error', 'Duration must be a whole number of days (>= 1).');
      return;
    }
    setBusy(true);
    try {
      // Send midnight Tbilisi for both. The API accepts ISO 8601.
      const startsIso = `${startsOn}T00:00:00+04:00`;
      const expiresIso = `${expiresOn}T00:00:00+04:00`;
      await issueSubscription(userId, {
        plan,
        hours_total: n,
        starts_at: startsIso,
        expires_at: expiresIso,
      });
      push('success', `Issued ${plan} (${n}h) to ${firstName}.`);
      onClose();
      onIssued();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Issue failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Issue subscription to ${firstName}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Plan</span>
          <select
            value={plan}
            onChange={(e) => onPlanChange(e.target.value as SubscriptionPlan)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${PS.border}`,
              borderRadius: 3,
              minHeight: 40,
              background: PS.white,
              ...T.ui,
            }}
          >
            <option value="starter">Starter (8h)</option>
            <option value="amateur">Amateur (16h)</option>
            <option value="advanced">Advanced (24h)</option>
            <option value="master">Master (40h)</option>
            <option value="unlimited">Unlimited (80h)</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={T.label}>Hours total</span>
          <input
            type="number"
            value={hours}
            min={1}
            onChange={(e) => setHours(e.target.value)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${PS.border}`,
              borderRadius: 3,
              minHeight: 40,
              ...T.ui,
            }}
          />
        </label>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* DatePicker's root is a native <input type="date">, so this
              <label> is genuinely (implicitly) associated with it through the
              component boundary — eslint-plugin-jsx-a11y can't see through
              custom components to verify that, hence the disable below.
              ariaLabel gives the control its own explicit accessible name too. */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 160px' }}>
            <span style={T.label}>Starts on</span>
            <DatePicker value={startsOn} onChange={setStartsOn} ariaLabel="Starts on" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 160px' }}>
            <span style={T.label}>Duration (days)</span>
            <input
              type="number"
              value={durationDays}
              min={1}
              step={1}
              onChange={(e) => setDurationDays(e.target.value)}
              style={{
                padding: '8px 10px',
                border: `1px solid ${PS.border}`,
                borderRadius: 3,
                minHeight: 40,
                ...T.ui,
              }}
            />
          </label>
        </div>

        <p style={{ ...T.caption, margin: 0 }}>
          {durationValid
            ? `Expires on ${expiresOn} (midnight Asia/Tbilisi). No linked payment is recorded (admin-issued).`
            : 'Enter a whole number of days (≥ 1).'}
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void handleIssue()} disabled={busy}>
            {busy ? 'Issuing…' : 'Issue subscription'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function addDays(yyyyMmDd: string, days: number): string {
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
